import "@thisbeyond/solid-select/style.css"
import { createEffect, createMemo, createSignal, on, onCleanup, onMount, Show, useContext, type Accessor, type Component } from "solid-js"
import { parse } from "valibot"
import type { Engine } from "../../../../lib/core/engine/Engine.type"
import { SelectionMode } from "../../../../lib/core/engine/SelectionMode/SelectionMode.enum"
import createSampleEntity from "../../../../lib/core/entity/createSampleEntity"
import { PlainEntityTypeMeta } from "../../../../lib/core/entity/EntityType/Plain"
import entityTypeMetaList from "../../../../lib/core/entity/entityTypeMetaList.generated"
import getEntityXYGridMap from "../../../../lib/core/entity/EntityXYGridMap/getEntityXYGridMap"
import type { Entity } from "../../../../lib/core/entity/index"
import nextEntityIdFromEngine from "../../../../lib/core/entity/nextEntityIdFromEngine"
import { byGroupIdAsc } from "../../../../lib/core/EntityTypeGroupId.enum"
import type { EntityTypeMeta } from "../../../../lib/core/EntityTypeMeta"
import findByPosition from "../../../../lib/core/findByPosition"
import type { PlayerIdOrNull } from "../../../../lib/core/player/PlayerIdOrNull"
import { createSetEntsAction } from "../../../../lib/core/state/flux/action/Map/SetEntsAction"
import dispatchClient from "../../../../lib/core/state/flux/dispatchClient"
import { StateSchema, type State } from "../../../../lib/core/state/State.type"
import coord from "../../../../lib/core/tile_position_xy/coord"
import type { TilePositionXY } from "../../../../lib/core/tile_position_xy/TilePositionXY.type"
import toCoord from "../../../../lib/core/tile_position_xy/toCoord"
import { byLayerIdAsc } from "../../../../lib/core/util/id"
import { deepClone } from "../../../../lib/deep_clone"
import Button from "../../../../lib/jsx/Button"
import flattenMapOfArrayToArray from "../../../../lib/ldsh/flattenMapOfArrayToArray"
import getID from "../../../../lib/ldsh/getID"
import MiniMap1Px from "../../../../lib/three/MiniMap/MiniMap1Px.component"
import isIdListSame from "../../../../rx/signal/equals/isIdListSame"
import { useSelectedPositionListSignal } from "../../../../rx/signal/useSelectedPositionListSignal"
import useSelectionModeSignal from "../../../../rx/signal/useSelectionModeSignal"
import type { Nullable } from "../../../../typescript"
import SelectSelectionMode from "../../../Btn/SelectSelectionMode"
import EngineContext from "../../../EngineContext"
import FormGroup from "../../../Form/FormGroup"
import FormLabel from "../../../Form/FormLabel"
import SelectEntityTypeMetaDropDownButton from "../../../SelectEntityTypeMetaDropDownButton"
import SelectEntityTypeMetaListDropDownButton from "../../../SelectEntityTypeMetaListDropDownButton"
import createFillTool from "./createFillTool"

enum FillTarget {
  MAP = 1,
  SELECTION = 2
}

enum ClobberOrProtect {
  CLOBBER = 0,
  PROTECT = 1
}

const FillTool: Component = () => {
  const engine: Engine = useContext(EngineContext)

  const fillTool = createFillTool(engine)
  onCleanup(fillTool.dispose)

  const [getSelectionMode, setSelectionMode] = useSelectionModeSignal(engine)
  const [selectedPositionList] = useSelectedPositionListSignal(engine)

  const [selectedEntityTypeMeta, setSelectedEntityTypeMeta] = createSignal<Nullable<EntityTypeMeta>>(PlainEntityTypeMeta)
  const [isPreviewing, setIsPreviewing] = createSignal(false)
  const [fillTarget, setFillTarget] = createSignal<FillTarget>(FillTarget.MAP)
  onMount(() => {
    if (getSelectionMode() === SelectionMode.MultiSelect) {
      setFillTarget(FillTarget.SELECTION)
    } else {
      setFillTarget(FillTarget.MAP)
    }
  })

  // get list of ETMs that are eligible for being clobbered
  const getClobberableEntityTypes = createMemo(() => {
    const etm = selectedEntityTypeMeta()
    if (etm) {
      const { layer_id } = etm
      return entityTypeMetaList.filter(etm => etm.layer_id === layer_id).sort(byLayerIdAsc).sort(byGroupIdAsc)
    }
    return []
  })
  createEffect(on(getClobberableEntityTypes, (newClobberableList) => {
    const prevNcl = getNoClobberList()
    const nextNcl = prevNcl.filter(etm => !newClobberableList.includes(etm))
    if (isIdListSame(prevNcl, nextNcl)) {
      return
    }
    setNoClobberList(nextNcl)
  }))

  const [getNoClobberList, setNoClobberList] = createSignal<Array<EntityTypeMeta>>([])

  // onMount(() => {
  //   // setSelectedEntityTypeMeta(SnowEntityTypeMeta)
  //   setSelectedEntityTypeMeta(entityTypeMetaList.find(etm => etm.id === EntityTypeId.Snow))
  //   setTimeout(() => {
  //     setIsPreviewing(true)
  //   }, 100)
  // })

  const playerId: PlayerIdOrNull = null

  const fillTargetLabel = createMemo(() => fillTarget() === FillTarget.SELECTION ? "Selection" : "Map")
  const isFillActionDisabled = createMemo(() => fillTarget() === FillTarget.SELECTION && selectedPositionList().length === 0)

  const previewState = createMemo(() => {
    const prevState = engine.state
    const state: State = deepClone(prevState)
    const etm = selectedEntityTypeMeta()
    if (etm) {
      const noClobberIdList = getNoClobberList().map(getID)
      const etmLayerId = etm.layer_id
      const { width, height } = state
      const skipPositions = new Set<TilePositionXY>()

      const isFillSelection = fillTarget() === FillTarget.SELECTION
      const selectedPositions = selectedPositionList()

      const grid = getEntityXYGridMap(state.ents)

      function clobberOrProtect(ent: Entity): ClobberOrProtect {
        if (isFillSelection && !findByPosition(selectedPositions, ent)) {
          skipPositions.add(toCoord(ent))
          return ClobberOrProtect.PROTECT
        }
        if (ent.layer_id === etmLayerId) {
          if (noClobberIdList.includes(ent.etype_id)) {
            skipPositions.add(toCoord(ent))
            return ClobberOrProtect.PROTECT
          }
          return ClobberOrProtect.CLOBBER
        }
        return ClobberOrProtect.PROTECT
      }

      for (const [xy, stack] of grid) {
        grid.set(xy, stack.filter(clobberOrProtect))
      }

      // forest is no longer L2
      // if (etm.id === EntityTypeId.Forest) {
      //   for (const [xy, stack] of grid) {
      //     if (!stack.find(requiredUnderEntForForest)) {
      //       skipPositions.add(xy)
      //     }
      //   }
      // }

      // state.ents = grid.fl
      // console.log('grid', grid)
      // console.log('flatMap', Array.from(grid).flatMap(([_, arr]) => arr))

      state.ents = flattenMapOfArrayToArray(grid)
      for (let x = 0; x < width; x++) {
        for (let y = 0; y < height; y++) {
          const position = coord(x, y)
          if (skipPositions.has(position)) {
            continue
          }
          const ent = createSampleEntity(etm.id, playerId, nextEntityIdFromEngine(state))
          ent.x = x
          ent.y = y
          state.ents.push(ent)
        }
      }
    }

    // state.ents = []
    return parse(StateSchema, state)
  })

  return <div class="w-24-rem">
    Fill Tool
    <hr />
    <div class="gap-2">
      <FormGroup>
        <FormLabel>Selection Mode</FormLabel>
        <SelectSelectionMode 
          value={getSelectionMode()}
          onChange={setSelectionMode}
        />
      </FormGroup>
    </div>
    <hr />
    <SelectEntityTypeMetaDropDownButton hr
      value={selectedEntityTypeMeta()}
      onChange={(event) => {
        // console.log('onChange', event)
        setSelectedEntityTypeMeta(event)
      }}
      list={entityTypeMetaList.toSorted(byLayerIdAsc).sort(byGroupIdAsc)}
      playerId={null}
      placeholder="Select Terrain or Unit"
    />
    <hr />
    No Clobber:
    <SelectEntityTypeMetaListDropDownButton hr
      value={getNoClobberList()}
      onChange={(event) => {
        console.log('onChange', event)
        setNoClobberList(event)
      }}
      list={getClobberableEntityTypes()}
      playerId={null}
      placeholder="Select Terrain or Unit"
    />
    <hr />
    <Show when={selectedEntityTypeMeta()} children={(selectedEntityTypeMeta: Accessor<EntityTypeMeta>) => {
      return <>
        {isPreviewing() && <div>
          <MiniMap1Px state={engine.state} size={16} />
          &nbsp;to:&nbsp;
          <MiniMap1Px state={previewState()} size={16} />
        </div>}
        <div class="input-group mb-2">
          <span class="input-group-text">Fill Target</span>
          <select class="form-select" onChange={event => {
            const value : FillTarget = (parseInt(event.currentTarget.value) as FillTarget) || FillTarget.MAP
            setFillTarget(value)
          }}>
            <option value={FillTarget.MAP} selected={fillTarget() === FillTarget.MAP}>Fill Map</option>
            <option value={FillTarget.SELECTION} selected={fillTarget() === FillTarget.SELECTION}>Fill Selection</option>
          </select>
        </div>
        <div class="btn-group">
          <Button class="btn btn-secondary" onClick={() => {
            setIsPreviewing(!isPreviewing())
          }}>Preview</Button>
          <Button class="btn btn-success" onClick={
            // eslint-disable-next-line solid/reactivity
            async () => {
              await dispatchClient(engine, createSetEntsAction(previewState().ents))
            }}
          disabled={isFillActionDisabled()}
          >Fill {fillTargetLabel()} With {selectedEntityTypeMeta().dname}</Button>
        </div>
      </>
    }} />
  </div>
}

export default FillTool
